(ns renolit-roofing-service.view.installer-add-observation-popup
  (:require [goog.object :as g]
            [re-frame.core :as rf]
            [reagent.core :as r]
            [renolit-roofing-service.config :as config]
            [renolit-roofing-service.layout.form :as layout.form]
            [renolit-roofing-service.layout.form.error :as form.error]
            [renolit-roofing-service.layout.icon :as layout.icon]
            [renolit-roofing-service.layout.info-bar :as layout.info-bar]
            [renolit-roofing-service.localization :refer [t]]
            [renolit-roofing-service.service.project :as srv.project]
            [renolit-roofing-service.service.service :as srv.service]
            [renolit-roofing-service.shared.domain.types :as dom.types]
            [renolit-roofing-service.tooltip :as tooltip]
            [renolit-roofing-service.tooltip.generic-popup :as tooltip.generic-popup]
            [renolit-roofing-service.util :as util]
            [renolit-roofing-service.util.file :as util.file]
            [cljc.java-time.instant :as jt.instant]
            ["@vis.gl/react-google-maps" :refer [APIProvider AdvancedMarker Map Pin]]))

(def ^:const view-context ::add-observation)

(def ^:const popup-id tooltip.generic-popup/default-popup-id)

(defn- choice-display-fn
  [{:keys [label-transl-kw]}]
  (t [label-transl-kw]))

(defn- initial-state
  [{:keys [wp wp-type review-id task-type zone-id lat lng]}]
  (merge
   {:wp wp
    :wp-type wp-type
    :review-id review-id
    :task-type task-type
    :zone-id zone-id
    :images []
    :location (when (and lat lng) {:lat lat :lng lng})}
   (case wp
     :visual-inspection {:observation nil
                         :incidence-level nil
                         :description ""}
     :non-destructive-testing {:dirt-type nil
                               :photo-position nil
                               :face nil
                               :description ""}
     {:description ""})))

(defn- valid-form?
  [{:keys [wp observation incidence-level dirt-type photo-position face]}]
  (case wp
    :visual-inspection (and observation incidence-level)
    :non-destructive-testing (and dirt-type photo-position face)
    :destructive-testing true
    false))

(rf/reg-event-db
 ::set-submitting
 (fn [db [_ v]]
   (assoc db ::submitting? v)))

(rf/reg-sub
 ::submitting?
 (fn [db _]
   (get db ::submitting? false)))

(rf/reg-event-fx
 ::submission-success
 (fn [_ _]
   {:fx [[:dispatch [::set-submitting false]]
         [:dispatch [::close]]]}))

(rf/reg-event-fx
 ::submission-failure
 (fn [_ _]
   {:fx [[:dispatch [::set-submitting false]]]}))

(def ^:const upload-timeout-ms 60000)

(def ^:const allowed-content-types
  #{"image/jpeg" "image/png" "image/webp"})

(rf/reg-fx
 ::s3-put
 (fn [{:keys [url file content-type on-success on-failure]}]
   (let [xhr (js/XMLHttpRequest.)
         fail! (fn []
                 (js/console.error "S3 PUT failed:" (.-status xhr) (.-responseText xhr))
                 (rf/dispatch on-failure))]
     (.open xhr "PUT" url)
     (.setRequestHeader xhr "Content-Type" content-type)
     (set! (.-timeout xhr) upload-timeout-ms)
     (set! (.-onload xhr)
           (fn []
             (if (<= 200 (.-status xhr) 299)
               (rf/dispatch on-success)
               (fail!))))
     (set! (.-onerror xhr) fail!)
     (set! (.-ontimeout xhr) fail!)
     (set! (.-onabort xhr) fail!)
     (.send xhr file))))

(defn- build-photo-id
  [observation image-index]
  (let [wp (some-> (:wp observation) name)
        wp-code (case wp
                  "non-destructive-testing" "NDT"
                  "visual-inspection" "VI"
                  "destructive-testing" "DT"
                  "OBS")
        obs-id (:id observation)
        short-id (subs (str obs-id) 0 (min 8 (count (str obs-id))))
        suffix (.fromCharCode js/String (+ (.charCodeAt "a" 0) (mod image-index 26)))]
    (str "PHOTO-" wp-code "-" short-id suffix)))

(defn- start-next-upload-dispatch
  [service-id observation images collected-keys]
  (let [next-image (first images)
        rest-images (rest images)
        object-id (str "observations/" (:id observation) "/" (random-uuid))
        image-index (count collected-keys)
        photo-id (build-photo-id observation image-index)
        content-type (:type next-image)]
    [:dispatch [::fetch-presigned-url service-id observation
                rest-images collected-keys next-image object-id photo-id content-type]]))

(rf/reg-event-fx
 ::presigned-url-received
 (fn [_ [_ service-id observation remaining-images collected-keys
         file content-type object-id photo-id {:keys [object-url]}]]
   {::s3-put {:url object-url
              :file file
              :content-type content-type
              :on-success [::upload-complete service-id observation
                           remaining-images (conj collected-keys {:photo-id photo-id
                                                                  :object-id object-id})]
              :on-failure [::upload-failed service-id observation
                           remaining-images collected-keys]}}))

(rf/reg-event-fx
 ::upload-failed
 (fn [_ [_ _service-id _observation _remaining-images _collected-keys]]
   {:fx [[:dispatch [::set-submitting false]]
         [:dispatch [::layout.info-bar/add-info-bar
                     {:context view-context
                      :type :error
                      :disappear-in-ms 5000
                      :msg-transl-kw :page.installer.add-observation-popup/upload-error}]]]}))

(rf/reg-event-fx
 ::upload-complete
 (fn [_ [_ service-id observation remaining-images collected-keys]]
   (if (seq remaining-images)
     {:fx [(start-next-upload-dispatch service-id observation remaining-images collected-keys)]}
     {:fx [[:dispatch [::srv.service/append-service-observation
                       service-id (assoc observation :images collected-keys)
                       {:on-success-evt [::submission-success]
                        :on-failure-evt [::submission-failure]}]]]})))

(rf/reg-event-fx
 ::fetch-presigned-url
 [(rf/inject-cofx :dev.gethop.session.re-frame.cognito.token/session)]
 (fn [{:keys [session]} [_ service-id observation remaining-images collected-keys image object-id photo-id content-type]]
   (if-not session
     {:fx [[:dispatch [::upload-failed service-id observation remaining-images collected-keys]]]}
     {:http-xhrio
      {:headers {"Authorization" (str "Bearer " (get-in session [:id-token :jwt]))}
       :method :post
       :uri (str "/api/services/" service-id "/images")
       :params {:object-id object-id
                :content-type content-type
                :operation :create}
       :format (util/ajax-transit-request-format)
       :response-format (util/ajax-transit-response-format)
       :on-success [::presigned-url-received service-id observation remaining-images
                    collected-keys (:content image) content-type object-id photo-id]
       :on-failure [::upload-failed service-id observation remaining-images collected-keys]}})))

(defn- build-ui-id
  [service-name obs-count]
  (let [next-num (inc obs-count)
        padded (let [s (str next-num)]
                 (if (< (count s) 5)
                   (str (apply str (repeat (- 5 (count s)) "0")) s)
                   s))]
    (str service-name "_" padded)))

(rf/reg-event-fx
 ::submit-observation
 (fn [{:keys [db]} [_ service-id form-state]]
   (let [{:keys [images wp review-id task-type location]} form-state
         service (get db :current-service)
         service-name (:name service)
         obs-count (count (or (:observations service) []))
         ui-id (build-ui-id service-name obs-count)
         lat-lng (when location [(:lat location) (:lng location)])
         base-obs (merge
                   {:id (str (random-uuid))
                    :ui-id ui-id
                    :wp wp
                    :review-id review-id
                    :task-type task-type
                    :clear false
                    :created-at (jt.instant/now)}
                   (when lat-lng {:lat-lng lat-lng})
                   (case wp
                     :visual-inspection
                     {:observation (:observation form-state)
                      :incidence-level (:incidence-level form-state)
                      :description (or (:description form-state) "")}
                     :non-destructive-testing
                     {:dirt-type (:dirt-type form-state)
                      :photo-position (:photo-position form-state)
                      :face (:face form-state)
                      :description (or (:description form-state) "")}
                     {:description (or (:description form-state) "")}))]
     {:fx [[:dispatch [::set-submitting true]]
           (if (seq images)
             (start-next-upload-dispatch service-id base-obs images [])
             [:dispatch [::srv.service/append-service-observation
                         service-id (assoc base-obs :images [])
                         {:on-success-evt [::submission-success]
                          :on-failure-evt [::submission-failure]}]])]})))

(defn observation-map
  [state style]
  (let [default-viewport @(rf/subscribe [::srv.project/default-map-viewport])
        location (:location @state)
        center (or location (:center default-viewport) {:lat 40.4168 :lng -3.7038})
        zoom (if location 19 (or (:zoom default-viewport) 17))]
    [:> Map
     {:mapId "OBSERVATION_LOCATION_MAP"
      :mapTypeId "satellite"
      :tilt 0
      :defaultZoom zoom
      :defaultCenter (clj->js center)
      :disableDefaultUI true
      :zoomControl true
      :keyboardShortcuts false
      :style style
      :onClick (fn [e]
                 (let [lat-lng (.. e -detail -latLng)
                       lat (.-lat lat-lng)
                       lng (.-lng lat-lng)]
                   (swap! state assoc :location {:lat lat :lng lng})))}
     (when location
       [:> AdvancedMarker
        {:position (clj->js location)}
        [:> Pin {:background "#FBBC04"
                 :borderColor "#333"
                 :glyphColor "#333"}]])]))

(defn- locate-on-map-section
  [state]
  (let [map-open? (r/atom (boolean (:location @state)))
        config (rf/subscribe [::config/config-data])]
    (fn [state]
      (let [location (:location @state)
            {:keys [google-maps-api-key]} @config]
        [:div.add-observation-popup__map-section
         [:div.add-observation-popup__section-header
          {:on-click #(swap! map-open? not)}
          [layout.icon/icon {:key :map}]
          [:span (t [:page.installer.add-observation-popup/locate-on-map])]]
         (when @map-open?
           (if google-maps-api-key
             [:div.add-observation-popup__map-container
              [:> APIProvider {:apiKey google-maps-api-key}
               [observation-map state {:height "200px" :width "100%" :border-radius "8px"}]]]
             [:div.add-observation-popup__map-container
              [:span "Map unavailable"]]))]))))

(defn- on-images-selected
  [state files]
  (let [valid-files (filter #(allowed-content-types (:type %)) files)]
    (when (not= (count files) (count valid-files))
      (rf/dispatch [::layout.info-bar/add-info-bar
                    {:context view-context
                     :type :error
                     :disappear-in-ms 5000
                     :msg-transl-kw :page.installer.add-observation-popup/invalid-file-type}]))
    (doseq [file valid-files]
      (util.file/get-file-content-as-base64
       file
       (fn [data-url]
         (swap! state update :images conj
                (assoc file :preview-url data-url)))))))

(defn- image-thumbnail
  [state idx image]
  [:div.add-observation-popup__thumbnail
   [:img {:src (:preview-url image)
          :alt (:name image)}]
   [:div.add-observation-popup__thumbnail-overlay
    [:span.add-observation-popup__thumbnail-id
     (str "ID: " (subs (str (:fe-id image)) 0 5))]]
   [:button.add-observation-popup__thumbnail-remove
    {:type "button"
     :on-click (fn []
                 (swap! state update :images
                        (fn [imgs] (vec (concat (subvec imgs 0 idx)
                                                (subvec imgs (inc idx)))))))}
    [layout.icon/icon {:key :close}]]])

(defn- file-input
  [state]
  [:input.add-observation-popup__file-input
   {:type "file"
    :multiple true
    :accept "image/jpeg,image/png,image/webp"
    :on-change (fn [e]
                 (let [browser-files (g/getValueByKeys e "target" "files")
                       files (map util.file/browser-file->file browser-files)]
                   (on-images-selected state files)
                   (set! (.. e -target -value) "")))}])

(defn- images-section
  [state]
  (let [images (:images @state)]
    [:div.add-observation-popup__images-section
     [:label.add-observation-popup__section-header
      [layout.icon/icon {:key :camera}]
      [:span (t [:page.installer.add-observation-popup/add-images])]
      [file-input state]]
     (if (seq images)
       [:div.add-observation-popup__thumbnails
        (doall
         (map-indexed
          (fn [idx image]
            ^{:key (or (:fe-id image) idx)}
            [image-thumbnail state idx image])
          images))]
       [:label.add-observation-popup__image-dropzone
        [layout.icon/icon {:key :camera}]
        [:span (t [:page.installer.add-observation-popup/add-images])]
        [file-input state]])]))

(defn- visual-inspection-fields
  [state]
  (let [observation-choices (dom.types/get-dropdown-values :observation)
        incidence-choices (dom.types/get-dropdown-values :incidence-level)]
    [:<>
     [layout.form/item
      {:context view-context
       :type :advanced-select-with-fixed-choices
       :search? false
       :multi? false
       :mandatory? true
       :state state
       :path [:observation]
       :attribute-path [:options :observation]
       :schema [:keyword]
       :choices observation-choices
       :choice-display-fn choice-display-fn
       :label-transl-kw :page.installer.add-observation-popup/observation
       :placeholder-transl-kw :form.advanced-select/select}]
     [layout.form/item
      {:context view-context
       :type :advanced-select-with-fixed-choices
       :search? false
       :multi? false
       :mandatory? true
       :state state
       :path [:incidence-level]
       :attribute-path [:options :incidence-level]
       :schema [:keyword]
       :choices incidence-choices
       :choice-display-fn choice-display-fn
       :label-transl-kw :page.installer.add-observation-popup/incidence-grade
       :placeholder-transl-kw :form.advanced-select/select}]]))

(defn- ndt-fields
  [state]
  (let [dirt-type-choices (dom.types/get-dropdown-values :dirt-types)
        photo-position-choices (dom.types/get-dropdown-values :photo-position)
        face-choices (dom.types/get-dropdown-values :face)]
    [:<>
     [layout.form/item
      {:context view-context
       :type :advanced-select-with-fixed-choices
       :search? false
       :multi? false
       :mandatory? true
       :state state
       :path [:dirt-type]
       :attribute-path [:options :dirt-type]
       :schema [:keyword]
       :choices dirt-type-choices
       :choice-display-fn choice-display-fn
       :label-transl-kw :page.installer.add-observation-popup/dirt-type
       :placeholder-transl-kw :form.advanced-select/select}]
     [layout.form/item
      {:context view-context
       :type :advanced-select-with-fixed-choices
       :search? false
       :multi? false
       :mandatory? true
       :state state
       :path [:photo-position]
       :attribute-path [:options :photo-position]
       :schema [:keyword]
       :choices photo-position-choices
       :choice-display-fn choice-display-fn
       :label-transl-kw :page.installer.add-observation-popup/photo-position
       :placeholder-transl-kw :form.advanced-select/select}]
     [layout.form/item
      {:context view-context
       :type :advanced-select-with-fixed-choices
       :search? false
       :multi? false
       :mandatory? true
       :state state
       :path [:face]
       :attribute-path [:options :face]
       :schema [:keyword]
       :choices face-choices
       :choice-display-fn choice-display-fn
       :label-transl-kw :page.installer.add-observation-popup/face
       :placeholder-transl-kw :form.advanced-select/select}]]))

(defn- main*
  [{:keys [service-id] :as opts}]
  (let [state (r/atom (initial-state opts))]
    (fn []
      (let [form-valid? (valid-form? @state)
            submitting? @(rf/subscribe [::submitting?])
            wp (:wp @state)]
        [:<>
         [tooltip.generic-popup/generic-popup-header
          {:title-transl-kw :page.installer.add-observation-popup/title
           :popup-id popup-id}]
         [:div.add-observation-popup
          (case wp
            :visual-inspection [visual-inspection-fields state]
            :non-destructive-testing [ndt-fields state]
            :destructive-testing nil
            nil)
          [locate-on-map-section state]
          [images-section state]
          [layout.form/item
           {:context view-context
            :type :text-area
            :state state
            :path [:description]
            :schema [:string]
            :label-transl-kw (if (= wp :non-destructive-testing)
                               :page.installer.add-observation-popup/sample-description
                               :page.installer.add-observation-popup/description)
            :input-opts {:rows 3}}]]
         [:div.add-observation-popup__footer
          [:button.btn.btn--primary.btn--full-width
           {:type "button"
            :disabled (or (not form-valid?) submitting?)
            :on-click (fn []
                        (when (and form-valid? (not submitting?))
                          (rf/dispatch [::submit-observation service-id @state])))}
           (if submitting?
             [:span.add-observation-popup__btn-spinner]
             (t [:page.installer.add-observation-popup/save]))]
          [:button.flat-btn.add-observation-popup__cancel-btn
           {:type "button"
            :on-click #(rf/dispatch [::close])}
           (t [:page.installer.add-observation-popup/cancel])]]]))))

(defn- main
  [opts]
  [main* opts])

(rf/reg-event-fx
 ::close
 (fn [_]
   {:fx [[:dispatch [::form.error/clear-errors view-context]]
         [:dispatch [::tooltip/destroy-by-id {:id popup-id}]]]}))

(rf/reg-event-fx
 ::open
 (fn [_ [_ opts]]
   {:fx [[:dispatch [::tooltip/register {:id popup-id
                                         :on-destroy-evt [::close]
                                         :component main
                                         :argv [opts]
                                         :class ["generic-popup-standard"]}]]]}))
